import { FEATURE_LAYERS_PROPERTIES } from '../constants';
import { WebMap } from '../constructors';
import { logDebug, logError, logInfo, logWarn } from '../../monitoring/log';
import { incrementContextLossCount, incrementContextRestoreCount } from './map-diagnostic-context';

export type MapSource = 'service' | 'sdk' | 'webgl' | 'layer' | 'user_action';

type MapLogParams = {
    source: MapSource;
    event: string;
    error?: Error;
    isEmbeddedMap?: boolean;
    extraData?: Record<string, unknown>;
};

function buildMapContext(
    source: MapSource,
    event: string,
    isEmbeddedMap?: boolean,
    extraData?: Record<string, unknown>
) {
    return {
        domain: 'map',
        mapSource: source,
        mapEvent: event,
        mapIsEmbedded: !!isEmbeddedMap,
        ...extraData,
    };
}

function getMapLogMessage(source: MapSource, event: string) {
    return `map: ${source}.${event}`;
}

export function logMapError({ source, event, error, isEmbeddedMap, extraData }: MapLogParams) {
    const message = getMapLogMessage(source, event);
    logError(error ?? new Error(message), buildMapContext(source, event, isEmbeddedMap, extraData));
}

export function logMapWarn({
    source,
    event,
    isEmbeddedMap,
    extraData,
}: Omit<MapLogParams, 'error'>) {
    logWarn(
        getMapLogMessage(source, event),
        buildMapContext(source, event, isEmbeddedMap, extraData)
    );
}

export function logMapInfo({
    source,
    event,
    isEmbeddedMap,
    extraData,
}: Omit<MapLogParams, 'error'>) {
    logInfo(
        getMapLogMessage(source, event),
        buildMapContext(source, event, isEmbeddedMap, extraData)
    );
}

export function logMapDebug({
    source,
    event,
    isEmbeddedMap,
    extraData,
}: Omit<MapLogParams, 'error'>) {
    logDebug(
        getMapLogMessage(source, event),
        buildMapContext(source, event, isEmbeddedMap, extraData)
    );
}

/**
 * Generates a letter key corresponding to the given index.
 * Keys start at A = 0, then proceed up until Z.
 * Numbers after 25 become AA, up to AZ, then on to BA, and so forth.
 * @param index The index
 * @returns The corresponding key
 */
export function getMapKey(index: number): string {
    const ordinateA = 'a'.charCodeAt(0);
    const ordinateZ = 'z'.charCodeAt(0);
    const len = ordinateZ - ordinateA + 1;

    let s = '';
    while (index >= 0) {
        s = String.fromCharCode((index % len) + ordinateA) + s;
        index = Math.floor(index / len) - 1;
    }
    return s;
}

export function getFeatureEntityIds(
    features: Array<{ attributes?: { entityId?: unknown } }>
): number[] {
    return features
        .map((feature) => feature.attributes?.entityId)
        .filter((entityId): entityId is number => typeof entityId === 'number');
}

/**
 * Converts any string into a unique 32-bit integer. SDBM Is a non-cryptographic
 * hash function that provides reasonable security against collisions. We use this
 * to create unique IDs based on a string input.
 * @param str The string to get the hash for.
 * @returns The numerical hash.
 */
export const sdbmHash = (str: string): number => {
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + (hash << 6) + (hash << 16) - hash;
    }

    return hash >>> 0;
};

export const moveLayerOnTop = (webMap: WebMap | undefined, layerKey: string) => {
    if (!webMap) {
        return;
    }
    const { layers } = webMap;
    const currentLayer = webMap.findLayerById(layerKey);
    const polylinesLayer = webMap.findLayerById(FEATURE_LAYERS_PROPERTIES.POLYLINES.layerKey);
    layers.reorder(polylinesLayer, layers.length - 1);
    layers.reorder(currentLayer, layers.length - 1);
};

export const setupWebGLContextListeners = (
    mapRef: React.MutableRefObject<HTMLDivElement | null>,
    isEmbeddedMap: boolean,
    onContextLostCallback?: (event: WebGLContextEvent, canvas: HTMLCanvasElement) => void
): (() => void) => {
    const canvases = mapRef.current?.querySelectorAll('canvas');
    if (!canvases || canvases.length === 0) {
        return () => {};
    }

    const cleanups: (() => void)[] = [];

    canvases.forEach((canvas) => {
        const onContextLost = (event: WebGLContextEvent) => {
            event.preventDefault();
            incrementContextLossCount();
            if (onContextLostCallback) {
                onContextLostCallback(event, canvas);
            } else {
                logMapError({
                    source: 'webgl',
                    event: 'context_lost',
                    isEmbeddedMap,
                    extraData: {
                        statusMessage: event.statusMessage,
                        canvasWidth: canvas.width,
                        canvasHeight: canvas.height,
                    },
                });
            }
        };

        const onContextRestored = () => {
            incrementContextRestoreCount();
            logMapInfo({
                source: 'webgl',
                event: 'context_restored',
                isEmbeddedMap,
            });
        };

        canvas.addEventListener('webglcontextlost', onContextLost as EventListener);
        canvas.addEventListener('webglcontextrestored', onContextRestored);

        cleanups.push(() => {
            canvas.removeEventListener('webglcontextlost', onContextLost as EventListener);
            canvas.removeEventListener('webglcontextrestored', onContextRestored);
        });
    });

    return () => cleanups.forEach((cleanup) => cleanup());
};
